import { useContext, useEffect } from 'react'

import { useLogCashWebCdfEvent } from '@cash-web/shared-feature-analytics'
import { ExperimentActivateView } from '@squareup/customer-data-events-web'

import { type Experiments } from '../Experiments'
import { DEFAULT_EXPERIMENTS, ExperimentsContext } from '../ExperimentsContext'

export const useExperiment = <T extends keyof Experiments>(
  experimentKey: T,
  options?: {
    /**
     * ⚠️ WARNING: DANGEROUS OPTION - USE WITH CAUTION! ⚠️
     *
     * Skipping exposure tracking breaks experiment integrity and should ONLY be used in very specific scenarios:
     * - When checking experiments in non-user-facing contexts (e.g., background tasks, prefetching logic)
     * - When conditionally checking an experiment value without actually exposing the user to the variant
     * - There is no discernable effect on the user experience
     *
     * DO NOT USE THIS:
     * - In regular UI components where the user sees the experiment variant
     * - To "simplify" your code or avoid tracking
     *
     * Misuse of this option will:
     * - Corrupt experiment metrics and statistical significance
     * - Make it impossible to determine the true impact of the experiment
     *
     * If you're unsure whether you need this, YOU DON'T. The default behavior,
     * tracking exposure, is safe and correct 99% of the time.
     *
     * Before using this option, ask yourself:
     * 1. Is the user actually seeing/experiencing this variant? If yes, DO NOT skip tracking.
     * 2. Am I working around conditionally rendering elements? If yes, refactor your components instead
     * 3. Am I working around really complicated rendering logic? It's too complicated, simplify it.
     *
     * If you choose to use this escape hatch, you should expect scrutiny when you submit your PR.
     */
    _DANGEROUSLY_SKIP_EXPOSURE_TRACKING?: boolean
  }
): Partial<Experiments>[T] | undefined => {
  const { experiments, emittedAssignmentEvent } = useContext(ExperimentsContext)
  if (process.env['NODE_ENV'] === 'development' && experiments === DEFAULT_EXPERIMENTS) {
    throw new Error('useExperiment must be used within ExperimentsProvider.')
  }

  const logCdfEvent = useLogCashWebCdfEvent()
  const selectedExperiment = experiments?.[experimentKey]
  const experimentValue = selectedExperiment?.variant_value

  useEffect(() => {
    if (
      options?._DANGEROUSLY_SKIP_EXPOSURE_TRACKING ||
      !emittedAssignmentEvent ||
      (emittedAssignmentEvent?.current[experimentKey] &&
        emittedAssignmentEvent.current[experimentKey] === experimentValue)
    ) {
      return
    }

    if (experimentValue) {
      emittedAssignmentEvent.current[experimentKey] = experimentValue
      void logCdfEvent(
        ExperimentActivateView({
          experimentId: experimentKey,
          variationId: experimentValue,
          experimentSource: 'AMPLITUDE',
        })
      )
    }
  })

  return selectedExperiment
}
